import React, { useCallback, useState } from 'react';
import { Col, Form, Input, Radio, RadioChangeEvent, Row, Select, Skeleton, Tooltip, message } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ButtonActionPage } from '../../../atoms';
import { ExportToCSV } from '../../../molecules/export/ExportToCSV';
import { ACCESS_CODE } from '../../../../constants/define/translation';
import {
  AccessCodeStatus,
  InputDuration,
  SubscriptionDurationValue,
  SubscriptionPlanValue,
} from '../../../../types/accessCode';
import { getAccessCodeRepository } from '../../../../repository/mytm/access-code/accessCodeRepository';
import { API_URLS } from '../../../../constants/urls/api-urls';
import { fileNameCsv, toAccessCodeListCsv, toSubscriptionValue } from '../../../../utils/transfer/access-code';

const { Option } = Select;

interface FormExportToCSVProps {
  onClose: () => void;
  purposeList?: any[];
}

export const FormExportToCSV = (props: FormExportToCSVProps) => {
  const { t } = useTranslation(ACCESS_CODE);
  const { onClose, purposeList } = props;
  const [form] = Form.useForm();

  const [csvData, setCsvData] = useState<any[]>();
  const [clicked, setClicked] = useState<boolean>(false);
  const [isInputDuration, setIsInputDuration] = useState(false);

  const resetFields = useCallback(() => {
    form.resetFields();
    setIsInputDuration(false);
  }, [form]);

  const onExportToCsv = useCallback(
    (values: any) => {
      setClicked(true);
      getAccessCodeRepository(API_URLS.ACCESS_CODE_LIST, {
        take: 99999,
        page: 1,
        status:
          values.status === AccessCodeStatus.ALL
            ? `${AccessCodeStatus.AVAILABLE},${AccessCodeStatus.ACTIVE},${AccessCodeStatus.INACTIVE},${AccessCodeStatus.EXPIRED}`
            : values.status,
        plan:
          Number(values.subscriptionPlan) !== SubscriptionPlanValue.ALL ? Number(values.subscriptionPlan) : undefined,
        duration: toSubscriptionValue(Number(values.subscriptionDuration), Number(values.inputDuration)),
        inputDuration:
          Number(values.subscriptionDuration) === SubscriptionDurationValue.INPUT ? InputDuration.INPUT : undefined,
        target: values.target,
      })
        .then(r => {
          onClose();
          setClicked(false);
          const toAccessCodeList = toAccessCodeListCsv(r.data, values.status);
          setCsvData(toAccessCodeList);
          resetFields();
          message.success(t('message.successExport'));
        })
        .catch(() => {
          onClose();
          setClicked(false);
          message.error(t('message.errorExport'));
        });
    },
    [onClose, resetFields],
  );

  const onSetCsvData = useCallback(() => {
    setCsvData(undefined);
  }, []);

  const handleOnChangeCheckDuration = useCallback((e: RadioChangeEvent) => {
    setIsInputDuration(e.target.value === SubscriptionDurationValue.INPUT);
  }, []);

  return (
    <>
      <Form layout="vertical" form={form} name="from-insights" onFinish={onExportToCsv}>
        <div className="form-content">
          <Form.Item label={t('form.status')} name="status" initialValue={AccessCodeStatus.ALL}>
            <Radio.Group style={fullWidth}>
              <Row>
                <Tooltip title={t('tooltip.exportAll')} placement="topLeft">
                  <Col span={4}>
                    <Radio value={AccessCodeStatus.ALL}>{AccessCodeStatus.ALL}</Radio>
                  </Col>
                </Tooltip>
                <Tooltip title={t('tooltip.exportCodeAvailable')} placement="topLeft">
                  <Col span={5}>
                    <Radio value={AccessCodeStatus.AVAILABLE}>{AccessCodeStatus.AVAILABLE}</Radio>
                  </Col>
                </Tooltip>
                <Tooltip title={t('tooltip.exportCodeActive')} placement="topLeft">
                  <Col span={5}>
                    <Radio value={AccessCodeStatus.ACTIVE}>{AccessCodeStatus.ACTIVE}</Radio>
                  </Col>
                </Tooltip>
                <Tooltip title={t('tooltip.exportCodeInactive')} placement="topLeft">
                  <Col span={5}>
                    <Radio value={AccessCodeStatus.INACTIVE}>{AccessCodeStatus.INACTIVE}</Radio>
                  </Col>
                </Tooltip>
                <Tooltip title={t('tooltip.exportCodeExpired')} placement="topLeft">
                  <Col span={5}>
                    <Radio value={AccessCodeStatus.EXPIRED}>{AccessCodeStatus.EXPIRED}</Radio>
                  </Col>
                </Tooltip>
              </Row>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={t('form.subscriptionPlan')}
            name="subscriptionPlan"
            initialValue={SubscriptionPlanValue.ALL}
          >
            <Radio.Group style={fullWidth}>
              <Row>
                <Tooltip title={t('tooltip.allPlan')} placement="topLeft">
                  <Col span={7}>
                    <Radio value={SubscriptionPlanValue.ALL}>{t('plan.all')}</Radio>
                  </Col>
                </Tooltip>
                <Tooltip title={t('tooltip.planChampion')} placement="topLeft">
                  <Col span={7}>
                    <Radio value={SubscriptionPlanValue.CHAMPION}>{t('plan.champion')}</Radio>
                  </Col>
                </Tooltip>
                <Tooltip title={t('tooltip.planLegend')} placement="topLeft">
                  <Col span={8}>
                    <Radio value={SubscriptionPlanValue.LEGEND}>{t('plan.legend')}</Radio>
                  </Col>
                </Tooltip>
              </Row>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={t('form.subscriptionDuration')}
            name="subscriptionDuration"
            initialValue={SubscriptionDurationValue.ALL}
          >
            <Radio.Group style={fullWidth} onChange={handleOnChangeCheckDuration}>
              <Row>
                <Tooltip title={t('tooltip.allDuration')} placement="topLeft">
                  <Col span={4}>
                    <Radio value={SubscriptionDurationValue.ALL}>{t('duration.all')}</Radio>
                  </Col>
                </Tooltip>
                <Tooltip title={t('tooltip.monthly')} placement="topLeft">
                  <Col span={5}>
                    <Radio value={SubscriptionDurationValue.MONTHLY}>{t('duration.monthly')}</Radio>
                  </Col>
                </Tooltip>
                <Tooltip title={t('tooltip.yearly')} placement="topLeft">
                  <Col span={5}>
                    <Radio value={SubscriptionDurationValue.YEARLY}>{t('duration.yearly')}</Radio>
                  </Col>
                </Tooltip>
                <Tooltip title={t('tooltip.inputRandom')} placement="topLeft">
                  <Col span={10}>
                    <Radio value={SubscriptionDurationValue.INPUT}>{t('duration.input')}</Radio>
                  </Col>
                </Tooltip>
              </Row>
            </Radio.Group>
          </Form.Item>
          {isInputDuration ? (
            <Row>
              <Col span={10} offset={14}>
                <Form.Item
                  name="inputDuration"
                  rules={[{ required: true, message: t('validate.subscriptionDurationBlank') }]}
                  className="input-duration"
                >
                  <Input placeholder={t('form.subscriptionDuration')} type="number" min={1} max={120} />
                </Form.Item>
              </Col>
            </Row>
          ) : (
            <Row>
              <Col span={10} offset={14}>
                <Form.Item name="inputDuration_hidden" className="input-duration input-invisibility">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          )}
          <Form.Item name="target" label={t('form.target')}>
            {!purposeList ? (
              <>
                <Skeleton active paragraph={{ rows: 0 }} />
              </>
            ) : (
              <Select
                showSearch
                placeholder={t('form.selectTarget')}
                optionFilterProp="children"
                filterOption={(input, option: any) => option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                allowClear
              >
                {purposeList.length > 0 &&
                  purposeList.map((item, index) => (
                    <Option value={item?.target || ''} key={item.index}>
                      <Tooltip title={item.target}>
                        <span className="option-purpose-data">{item.target}</span>
                      </Tooltip>
                    </Option>
                  ))}
              </Select>
            )}
          </Form.Item>
        </div>
        <ButtonActionPage
          text={t('btnExport')}
          id="1"
          className={`btn-submit-export-access-code ${clicked ? 'pointer-none' : 'none'}`}
          icon={<ExportOutlined />}
          htmlType="submit"
        />
        <ExportToCSV data={csvData} fileName={fileNameCsv()} onSetCsvData={onSetCsvData} />
      </Form>
    </>
  );
};

const fullWidth = {
  width: '100%',
};
